import {
  Add,
  DirectionsCarFilled,
  FilterAltOff,
  Login,
  Logout,
  MeetingRoom,
  PortraitSharp,
  RemoveRedEyeSharp,
  Settings,
  TextSnippet,
} from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import ButtonActionsComponent from 'components/ButtonActionsComponent';
import ButtonComponent from 'components/ButtonComponent';
import ConfirmDeleteModalComponent from 'components/ConfirmDeleteModalComponent';
import DeviceEditionComponent from 'components/DeviceEditionComponent';
import TableComponent, { IColumnProps } from 'components/TableComponent';
import { AppContext } from 'context/AppContextProvider';
import React, { useContext, useEffect, useState } from 'react';
import {
  useDeleteDeviceByIdAsync,
  useGetAllControllersByLicenseCountAsync,
  useGetDeviceByIdAsync,
} from 'services/Devices/hooks/useMutation';
import {
  IDeviceFilterParams,
  IDeviceProps,
  IDeviceSimpleProps,
} from 'services/Devices/types';
import { IPaginationParams } from 'services/types';
import DeviceDetailsByDiscriminator from '../_components/DeviceDetailsByDiscriminator';
import BrandIcons from '../_components/BrandIcons';
import DeviceConfigModal from '../_components/DeviceConfigModal';
import ControllersFilterModal from './_components/ControllersFilterModal';
import { useGetControlIdDDeviceByIdAsync } from '../../../services/Devices/Recognition/Controllers/ControlId/hooks/useMutation';
import { useGetFacialControllerByIdAsync } from '../../../services/Devices/Recognition/Controllers/hooks/useMutation';
import LogsModalComponent from '../../../components/LogsComponent';
import IsOnlineComponent from '../../../components/IsOnlineComponent';
import { getStreamByEndpointAsync } from '../../../services/Events';
import { DEVICE_CONTROLLER_ENDPOINT } from '../../../services/Devices';
import DeviceLogStream from '../_components/DeviceLogStream';
import HeaderDashboard from 'pages/DashboardPageClient/_components/_ui/Header';
import { t } from 'i18next';

const ControllersPage = () => {
  const [isVisibleEditionModal, setIsVisibleEditionModal] = useState(false);
  const [isLoadingControllers, setIsLoadingControllers] =
    useState<boolean>(false);

  const [filter, setFilter] = useState<IDeviceFilterParams>({
    name: '',
  });
  const [isVisibleFilterModal, setIsVisibleFilterModal] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<IDeviceProps>(
    {} as IDeviceProps,
  );
  const [selectedItemToDelete, setSelectedItemToDelete] =
    useState<IDeviceSimpleProps>();
  const [isVisibleDeleteItemModal, setIsVisibleDeleteItemModal] =
    useState<boolean>(false);
  const [devicePaginationParams, setDevicePaginationParams] =
    useState<IPaginationParams>({ pageNumber: 1, pageSize: 10 });
  const [isVisibleConfigModal, setIsVisibleConfigModal] =
    useState<boolean>(false);
  const [tableResetKey, setTableResetKey] = useState(0);
  const { showSnackbar } = useContext(AppContext);
  const { mutateAsync: mutateGetDeviceById } = useGetDeviceByIdAsync();
  const [logModal, setLogModal] = useState<{
    isVisible: boolean;
    deviceId: number;
  }>({ isVisible: false, deviceId: 0 });
  const [devices, setDevices] = useState<IDeviceSimpleProps[]>([]);
  const [devicesCount, setDevicesCount] = useState<number>(0);

  const functionTypeLabels: Record<string, string> = {
    ENTRY: t('controllersPage.functionTypeOptions.entry'),
    EXIT: t('controllersPage.functionTypeOptions.exit'),
    COMMON: t('controllersPage.functionTypeOptions.common'),
    TAG: t('controllersPage.functionTypeOptions.tag'),
  };

  const functionTypeIcons: Record<string, React.ReactElement> = {
    ENTRY: <Login fontSize="small" />,
    EXIT: <Logout fontSize="small" />,
    COMMON: <MeetingRoom fontSize="small" />,
    TAG: <DirectionsCarFilled fontSize="small" />,
  };

  const columns: IColumnProps[] = [
    { label: 'Ref.', align: 'center', id: 'id', width: 0 },
    {
      label: t('common.brand'),
      width: 0,
      align: 'center',
      id: 'brand',
      render(val: any) {
        return <BrandIcons brand={val} />;
      },
    },
    {
      label: t('common.name'),
      id: 'id',
      sortId: 'name',
      render(value, rowIndex) {
        const searched = devices.find((v) => v.id == Number.parseInt(value));
        if (searched) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.3 }}>
              <IsOnlineComponent
                width={18}
                height={18}
                isOnline={Boolean(searched.isOnline)}
              />
              {searched.name}
            </Box>
          );
        }
        return <></>;
      },
    },
    { label: t('controllersPage.ip'), id: 'ip' },
    {
      label: t('controllersPage.functionType'),
      id: 'functionType',
      render(val: any) {
        if (!val) return <>-</>;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
            {functionTypeIcons[val]}
            {functionTypeLabels[val] ?? val}
          </Box>
        );
      },
    },
    {
      id: 'id',
      label: t('common.actions'),
      align: 'center',
      width: 0,
      disableSort: true,
      render: (id) => (
        <ButtonActionsComponent
          isWithDefaultOptionsList
          actionOptions={actionOptions(Number(id))}
          handleDelete={() => handleDeleteItem(Number(id))}
          handleEdit={() => handleEdit(Number(id))}
        />
      ),
    },
  ];

  const {
    mutateAsync: mutateDeleteDeviceById,
    isLoading: isLoadingDeleteDeviceById,
  } = useDeleteDeviceByIdAsync();

  const actionOptions = (id: number) => {
    return [
      {
        label: t('actionsOptions.configuration'),
        icon: <Settings />,
        onClick: () => {
          setIsVisibleConfigModal(true);
          handleConfig(id);
        },
      },
      {
        label: 'Logs',
        icon: <TextSnippet />,
        onClick: () => {
          setLogModal({ isVisible: true, deviceId: id });
        },
      },
    ];
  };

  const brands: any[] = [
    { label: 'Intelbras', value: 'INTELBRAS' },
    { label: 'Control ID', value: 'CONTROLID' },
    { label: 'Hikvision', value: 'HIKVISION' },
    { label: 'TopData', value: 'TOPDATA' },
  ];

  const { mutateAsync: mutateGetAllControllersCount } =
    useGetAllControllersByLicenseCountAsync();

  const { mutateAsync: mutateGetControlIdDDeviceByIdAsync } =
    useGetControlIdDDeviceByIdAsync();

  const { mutateAsync: mutateGetFacialControllerByIdAsync } =
    useGetFacialControllerByIdAsync();

  const handleConfig = async (id: number) => {
    const dev = devices.find((v) => v.id === id);
    if (dev) {
      switch (dev.discriminator) {
        case 'FacialControllerEntity':
          await mutateGetFacialControllerByIdAsync(dev.id).then((val) => {
            val.discriminator = 'FacialControllerEntity';
            setSelectedDevice(val);
          });
          break;
        case 'ControlIdDeviceEntity':
          await mutateGetControlIdDDeviceByIdAsync(dev.id).then((val) => {
            val.discriminator = 'ControlIdDeviceEntity';
            setSelectedDevice(val);
          });
          break;
        default:
          console.warn('Discriminator não tratado:', dev.discriminator);
      }
    }
    setIsVisibleConfigModal(true);
  };

  const handleCancelDeletion = () => {
    setIsVisibleDeleteItemModal(false);
    setSelectedItemToDelete(undefined);
  };

  const handleDeleteItem = async (id: number) => {
    const device: any = await mutateGetDeviceById(id);
    setSelectedItemToDelete(device);
    setIsVisibleDeleteItemModal(true);
  };

  const handleEdit = (id: number) => {
    mutateGetDeviceById(id).then(setSelectedDevice);
    setIsVisibleEditionModal(true);
  };

  const handleConfirmDeletion = () => {
    if (selectedItemToDelete?.id)
      mutateDeleteDeviceById(selectedItemToDelete?.id).then(() => {
        setDevicePaginationParams((oldStt) => ({ ...oldStt, pageNumber: 1 }));
        setIsVisibleDeleteItemModal(false);
        showSnackbar('Controlador excluído com sucesso.', 'success');
        getDevices(devicePaginationParams);
      });
  };

  const getDevices = (pagination: IPaginationParams) => {
    getStreamByEndpointAsync(
      DEVICE_CONTROLLER_ENDPOINT,
      setDevices,
      pagination,
      undefined,
      setIsLoadingControllers,
      undefined,
      true,
      filter,
    );
    mutateGetAllControllersCount({ filter }).then(setDevicesCount);
  };

  const handleFilterChange = (newFilter: Partial<IDeviceFilterParams>) => {
    setFilter((old) => {
      const merged: IDeviceFilterParams = { ...old, ...newFilter };
      (Object.keys(merged) as (keyof IDeviceFilterParams)[]).forEach((key) => {
        if (merged[key] === undefined) delete merged[key];
      });
      return merged;
    });
    setDevicePaginationParams((old) => ({ ...old, pageNumber: 1 }));
  };

  const handleClearFilters = () => {
    setFilter({ name: '' });
    setDevicePaginationParams((old) => ({
      pageNumber: 1,
      pageSize: old.pageSize,
    }));
    setTableResetKey((old) => old + 1);
  };

  const activeFilterCount = [
    !!filter.name,
    !!filter.brands?.length,
    filter.isOnline !== undefined,
    !!filter.functionTypes?.length,
  ].filter(Boolean).length;

  const additionalButton = (
    <Box sx={{ display: 'flex', gap: 1.5 }}>
      <ButtonComponent
        startIcon={<Add />}
        size="small"
        onClick={() => setIsVisibleEditionModal(true)}
      >
        {t('controllersPage.addController')}
      </ButtonComponent>

      <ButtonComponent
        onClick={handleClearFilters}
        size="small"
        color="warning"
        startIcon={<FilterAltOff />}
      >
        {t('controllersPage.filter.clear')}
      </ButtonComponent>
    </Box>
  );

  const handleSortChange = (property: string, direction: 'asc' | 'desc') => {
    setDevicePaginationParams((old) => ({
      ...old,
      sortBy: property,
      sortDirection: direction,
    }));
  };

  useEffect(() => {
    getDevices(devicePaginationParams);
  }, [devicePaginationParams]);

  return (
    <>
      <HeaderDashboard></HeaderDashboard>
      <TableComponent
        key={tableResetKey}
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PortraitSharp color="success" />
            <Typography fontSize={19}>{t('controllersPage.title')}</Typography>
          </Box>
        }
        expandedRowContent={(row: any) => <DeviceLogStream deviceId={row.id} />}
        columns={columns}
        isExpandedRow
        totalCount={devicesCount}
        onChangePageOrRowsPerPage={(param) => {
          setDevicePaginationParams(param);
          getDevices(param);
        }}
        searchValue={filter.name}
        onSubmitMainFilter={() => getDevices(devicePaginationParams)}
        onChangeMainFilter={(val) =>
          setFilter((oldSt) => ({ ...oldSt, name: val }))
        }
        onOpenFilter={() => setIsVisibleFilterModal(true)}
        activeFilterCount={activeFilterCount}
        isEnableSort
        serverSort
        onSortChange={handleSortChange}
        rows={devices}
        additionalTitleComponent={additionalButton}
      />

      <ControllersFilterModal
        isOpen={isVisibleFilterModal}
        onClose={() => setIsVisibleFilterModal(false)}
        currentFilter={filter}
        onFilter={handleFilterChange}
        activeFilterCount={activeFilterCount}
      />

      <DeviceEditionComponent
        isOpen={isVisibleEditionModal}
        deviceToEdit={selectedDevice}
        isControllers
        handleSaveForms={(_, reset) => {
          getDevices(devicePaginationParams);
          setIsVisibleEditionModal(false);
          setSelectedDevice({} as IDeviceProps);
          reset();
        }}
        setDeviceToEdit={setSelectedDevice}
        discriminator={selectedDevice?.discriminator}
        availableEvents={
          DeviceDetailsByDiscriminator(selectedDevice?.discriminator)
            ?.availableEvents ?? []
        }
        setIsOpen={setIsVisibleEditionModal}
        brands={brands}
      />

      <LogsModalComponent
        deviceId={logModal.deviceId}
        isVisible={logModal.isVisible}
        setIsVisible={(state) =>
          setLogModal((oldStt) => ({ ...oldStt, isVisible: state }))
        }
      />

      <DeviceConfigModal
        discriminator={selectedDevice?.discriminator}
        isOpen={isVisibleConfigModal}
        selectedDevice={selectedDevice}
        setSelectedDevice={setSelectedDevice}
        setIsOpen={setIsVisibleConfigModal}
        onSaved={() => getDevices(devicePaginationParams)}
      />

      <ConfirmDeleteModalComponent
        isLoading={isLoadingDeleteDeviceById}
        isVisibleDeleteDialog={isVisibleDeleteItemModal}
        setIsVisibleDeleteDialog={setIsVisibleDeleteItemModal}
        title={'Excluir Dispositivo'}
        message={`Tem certeza que deseja excluir o controlador "${selectedItemToDelete?.name}"?`}
        handleCancelDeletion={handleCancelDeletion}
        handleConfirmDeletion={handleConfirmDeletion}
      />
    </>
  );
};

export default ControllersPage;
